import * as PIXI from 'pixi.js';
import GameConfig from '../GameConfig';
import State from './State';
import {WallPiece} from 'src/common/map/WallPiece';
import {Countdown} from '../Countdown';
import {clamp} from '../../utils';
import {Loc} from '../../common/map/Location';
import {WallPieceCursor} from '../WallPieceCursor';
import {Location} from '../../common/map/Location';
import {BuildWall} from '../actions/BuildWall';
import {pointerRenderer} from 'src/Pixi/PointerRenderer';
import {gridRenderer} from 'src/Pixi/map/GridRenderer';
import {buildabilityRenderer} from 'src/Pixi/map/BuildabilityRenderer';

import Point = PIXI.Point;

const counterclockwiseKeys = ['ArrowLeft', 'ArrowUp', 'KeyA'];
const clockwiseKeys = ['ArrowRight', 'ArrowDown', 'KeyD'];

class BuildState extends State {
    private cursor: WallPieceCursor;
    private keyHandler: (event: KeyboardEvent) => void;
    private countdown: Countdown;
    private tiles: Loc[];

    public start(): void {
        this.game.clearDamagedWalls();

        // Add the wall piece cursor
        if (!this.isSpectating()) {
            this.cursor = new WallPieceCursor(this.game);
            this.updateWallPiece();

            this.keyHandler = this.onKeydown.bind(this);
            document.addEventListener('keydown', this.keyHandler);

            this.controller.hideCursor();
        }

        this.countdown = new Countdown(20, 'Build Walls');
        this.countdown.start();
        pointerRenderer.setPointerType('pointer');
        this.controller.enterBuildState();
        gridRenderer.showForMap(this.game.map);
        buildabilityRenderer.show(this.game);
    }

    private onKeydown(event: KeyboardEvent): void {
        if (this.isSpectating()) return;

        if (counterclockwiseKeys.includes(event.code)) {
            this.rotateWallPieceCounterclockwise();
        } else if (clockwiseKeys.includes(event.code)) {
            this.rotateWallPieceClockwise();
        }
    }

    public finish(): void {
        this.countdown.finish();

        // Always do this because we are probably marked as "dead" by the server when we finish this state
        document.removeEventListener('keydown', this.keyHandler);
        if (!this.isSpectating()) {
            this.cursor.remove();
        }
        this.controller.showCursor();
        this.controller.exitBuildState();
        gridRenderer.hide();
        buildabilityRenderer.hide();
    }

    public tick(): void {
        if (!this.isSpectating()) {
            this.updateWallPiece();
        }

        this.countdown.tick();
    }

    public onMouseMove(pos: Point): void {
        if (this.isSpectating()) return;

        const tile = this.tilePosition(pos);
        this.cursor.updatePosition(tile, this.game.map, this.game.index);
    }

    private tilePosition({x, y}: Loc): Point {
        const tileX = clamp(Math.floor(x / GameConfig.tileSize), 0, this.game.map.width - 1);
        const tileY = clamp(Math.floor(y / GameConfig.tileSize), 0, this.game.map.height - 1);
        return new Point(tileX, tileY);
    }

    public onMouseClick(_e: Point): void {
        if (this.isSpectating()) return;

        if (this.cursor.canBuild && this.cursor.wallPiece) {
            new BuildWall(this.game).byClient(
                new Location(this.cursor.tile.x, this.cursor.tile.y),
                this.cursor.wallPiece
            );
            this.cursor.remove();
        }
    }

    private updateWallPiece(): void {
        const player = this.game.currentPlayer;
        if (player.wallPiece !== this.tiles) {
            this.tiles = player.wallPiece;
            this.cursor.updatePiece(
                new WallPiece(player.wallPiece),
                this.game.map,
                this.game.index
            );
        }
    }

    public rotateWallPieceCounterclockwise(): void {
        this.cursor.rotate('counterclockwise', this.game.map, this.game.index);
    }

    public rotateWallPieceClockwise(): void {
        this.cursor.rotate('clockwise', this.game.map, this.game.index);
    }
}

export default BuildState;



// WEBPACK FOOTER //
// ./src/game/states/BuildState.ts